import { Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import type { ConcertResponse, MatchResponse } from 'api/responseTypes';
import { ReactComponent as PlaceholderTeamLogo } from 'assets/placeholder.svg';
import { SlotDescriptionBadge } from 'components/SlotDescriptionBadge/SlotDescriptionBadge';
import { useCartContext } from 'context/CartContext/CartContext';
import { formatCheckoutDateTime } from 'utils/timeslot';

type EventSummaryCardProps = {
  matchData?: MatchResponse;
  concertData?: ConcertResponse;
};

// `fontFamily` omitted deliberately — the page wrapper sets the Thmanyah face and this inherits it.
const metaTextStyle = {
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '150%',
  letterSpacing: '0.01em',
  textAlign: 'right',
} as const;

const cardStyle = {
  bg: 'white',
  p: { base: '16px', md: '20px' },
  borderRadius: '16px',
  borderWidth: '1px',
  borderColor: 'dark.D100',
  boxShadow: '0 5px 5px rgba(0, 0, 0, 0.02)',
} as const;

/**
 * Event header shown at the top of the checkout content column. Renders the concert poster
 * (or match team logos) alongside the title, venue and date/time. Split out of the old
 * TicketSummary so the price/terms sidebar no longer carries event data.
 */
export const EventSummaryCard = ({ matchData, concertData }: EventSummaryCardProps) => {
  const { selectedTimeslot } = useCartContext();

  const isMatch = Boolean(matchData?.teamA && matchData?.teamB);
  const event = matchData ?? concertData;
  if (!event) return null;

  const dateTimeDisplay = formatCheckoutDateTime(selectedTimeslot, event.time, event.date);
  const venue = [event.stadium, event.city].filter(Boolean).join(' - ');

  return (
    <Flex {...cardStyle} dir='rtl' align='center' justify='space-between' gap='16px'>
      {isMatch ? (
        <HStack spacing='4px' flexShrink={0}>
          <Image
            src={matchData?.teamA.logo}
            alt={matchData?.teamA.name ?? ''}
            boxSize='48px'
            borderRadius='50%'
            objectFit='contain'
            fallback={<PlaceholderTeamLogo />}
          />
          <Image
            src={matchData?.teamB.logo}
            alt={matchData?.teamB.name ?? ''}
            boxSize='48px'
            borderRadius='50%'
            objectFit='contain'
            fallback={<PlaceholderTeamLogo />}
          />
        </HStack>
      ) : (
        <Image
          src={concertData?.imageLink}
          alt={concertData?.title ?? ''}
          w='100px'
          h='100px'
          borderRadius='10px'
          objectFit='cover'
          flexShrink={0}
          fallback={<PlaceholderTeamLogo />}
        />
      )}

      <VStack align='stretch' spacing='6px' flex='1' textAlign='right'>
        <Text
          color='dark.D900'
          fontWeight={700}
          fontSize='16px'
          lineHeight='100%'
          letterSpacing='0'
          textAlign='right'
        >
          {isMatch ? `${matchData?.teamA.name} - ${matchData?.teamB.name}` : concertData?.title}
        </Text>
        {venue && (
          <Text {...metaTextStyle} color='dark.D500'>
            {venue}
          </Text>
        )}
        <Text {...metaTextStyle} color='dark.D500'>
          {dateTimeDisplay}
        </Text>
        {selectedTimeslot?.slotDescription && (
          <SlotDescriptionBadge description={selectedTimeslot.slotDescription} />
        )}
      </VStack>
    </Flex>
  );
};
